import { FC, useEffect, useRef, useState } from "react";
import CustomButton from "../../UI/Buttons/CustomButton";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Checkbox from "../../UI/Inputs/Checkbox";
import { useFormik } from "formik";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { isFulfilled } from "@reduxjs/toolkit";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import {
  getClientSettingsDataThunk,
  submitClientNotificationSettings,
} from "../../../store/centers/thunks";
import { TClientSettings } from "../../../store/centers/types";
import UnsavedChangesModal from "../../Candidate/Settings/UnsavedChangesModal";
import { useNavigationPrompt } from "../../../hooks/useNavigationPrompt";
import { getTimeZoneInfo } from "../../../utils/dataParser";

type NotificationType =
  | "candidate_accepted_scheduled_vacancy"
  | "candidate_accepted_urgent_vacancy"
  | "candidate_applied_scheduled_vacancy"
  | "candidate_applied_urgent_vacancy"
  | "candidate_declined_job"
  | "pending_timesheet_approval";

type Channel = "sms" | "email" | "daily_summary_email" | "weekly_summary_email";

type ClientPreferences = {
  [key in NotificationType]?: Channel[];
};

const Settings: FC = () => {
  const dispatch = useDispatch();

  //Selector declaration
  const user = useSelector((state) => state.auth.user);
  const clientNotificationSettings = useSelector(
    (state) => state.Centres.clientNotificationSettings
  );

  const initialPreferencesRef = useRef<ClientPreferences>({});

  const [preferences, setPreferences] = useState<ClientPreferences>({});
  const [
    candidateAcceptedScheduledVacancyChecked,
    setCandidateAcceptedScheduledVacancyChecked,
  ] = useState(false);
  const [
    candidateAcceptedUrgentVacancyChecked,
    setCandidateAcceptedUrgentVacancyChecked,
  ] = useState(false);
  const [
    candidateAppliedScheduledVacancyChecked,
    setCandidateAppliedScheduledVacancyChecked,
  ] = useState(false);
  const [
    candidateAppliedUrgentVacancyChecked,
    setCandidateAppliedUrgentVacancyChecked,
  ] = useState(false);
  const [saveButtonText, setSaveButtonText] = useState<string>("Save Changes");
  const [showConfirmUnSaved, setShowConfirmUnSaved] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  // Set initial preferences on first load from DB
  useEffect(() => {
    const savePreferences = convertToPreferences(
      clientNotificationSettings || []
    );
    initialPreferencesRef.current = savePreferences.preferences;
    // ...existing code...
  }, [clientNotificationSettings]);

  useEffect(() => {
    // Compare function
    const is_dirty =
      JSON.stringify(preferences) !==
      JSON.stringify(initialPreferencesRef.current);
    setIsDirty(is_dirty);
  }, [preferences, initialPreferencesRef]);

  useNavigationPrompt(isDirty, (nextAction) => {
    setPendingAction(nextAction); // nextAction = "back", "forward", "reload", or pathname
    setShowConfirmUnSaved(true);
  });

  // Handle channel checkbox changes for notification preferences
  const handleCheckboxChange = (
    type: NotificationType,
    channel: Channel,
    isChecked: boolean
  ) => {
    // Block adding channels to matching_jobs_posted if parent checkbox is unchecked
    if (
      type === "candidate_accepted_scheduled_vacancy" &&
      !candidateAcceptedScheduledVacancyChecked
    )
      return;
    if (
      type === "candidate_accepted_urgent_vacancy" &&
      !candidateAcceptedUrgentVacancyChecked
    )
      return;
    if (
      type === "candidate_applied_scheduled_vacancy" &&
      !candidateAppliedScheduledVacancyChecked
    )
      return;
    if (
      type === "candidate_applied_urgent_vacancy" &&
      !candidateAppliedUrgentVacancyChecked
    )
      return;

    setPreferences((prev) => {
      const current = prev[type] || [];

      const updated = isChecked
        ? Array.from(new Set([...current, channel]))
        : current.filter((c) => c !== channel);

      const next = {
        ...prev,
        [type]: updated,
      };

      return cleanPreferences(next); // Remove empty arrays
    });
  };

  // Removing empty arrays from preferences
  const cleanPreferences = (prefs: ClientPreferences): ClientPreferences => {
    const cleaned: ClientPreferences = {};
    for (const key in prefs) {
      const arr = prefs[key as NotificationType];
      if (arr && arr.length > 0) {
        cleaned[key as NotificationType] = arr;
      }
    }
    return cleaned;
  };

  const handleVacancyTypeChange = (
    type:
      | "candidate_accepted_scheduled_vacancy"
      | "candidate_accepted_urgent_vacancy"
      | "candidate_applied_scheduled_vacancy"
      | "candidate_applied_urgent_vacancy",
    setChecked: (checked: boolean) => void,
    isChecked: boolean
  ) => {
    setChecked(isChecked);

    // setPreferences((prev) => {
    //   const newPrefs = { ...prev };

    //   if (isChecked) {
    //     newPrefs[type] = [];
    //   } else {
    //     delete newPrefs[type];
    //   }

    //   return newPrefs;
    // });

    /** this helps to won't disturb weekly_summary_email and daily_summary_email */
    setPreferences((prev) => {
      const newPrefs = { ...prev };

      const existing = prev[type] || [];

      // ✅ Preserve only summary channels
      const preserved = existing.filter(
        (channel) =>
          channel !== "daily_summary_email" &&
          channel !== "weekly_summary_email"
      );

      if (
        type === "candidate_accepted_urgent_vacancy" ||
        type === "candidate_applied_urgent_vacancy"
      ) {
        if (preserved.length > 0) {
          newPrefs[type] = preserved;
        } else {
          delete newPrefs[type];
        }
      } else {
        // For scheduled types, use default behavior
        if (isChecked) {
          newPrefs[type] = [];
        } else {
          delete newPrefs[type];
        }
      }

      return newPrefs;
    });
  };

  const { handleSubmit, setFieldValue } = useFormik<any>({
    validationSchema: createValidationSchema("candidate_save_settings"),
    initialValues: {
      preferences: {} as ClientPreferences,
    },
    onSubmit: async (values: any) => {
      dispatch(showTransparentPreloaderAction());
      const response = await dispatch(submitClientNotificationSettings(values));
      if (isFulfilled(response)) {
        dispatch(showNotificationAction("Saved"));
        setIsDirty(false);
        setTimeout(() => {
          setSaveButtonText("Saved!");
        }, NOTIFICATION_TIME_OUT);
      }
      dispatch(hideTransparentPreloaderAction());
    },
  });

  // Update preferences when they change
  useEffect(() => {
    setFieldValue("preferences", preferences);
    setSaveButtonText("Save Changes");
  }, [preferences, setFieldValue]);

  useEffect(() => {
    dispatch(showTransparentPreloaderAction());
    dispatch(getClientSettingsDataThunk());
    dispatch(hideTransparentPreloaderAction());
  }, []);

  // Converting API response to preferences format
  const convertToPreferences = (
    data: TClientSettings[]
  ): { preferences: ClientPreferences } => {
    const preferences: ClientPreferences = {};

    data?.length > 0 &&
      data.forEach(({ notification_type, channel }) => {
        const key = notification_type as NotificationType;
        if (!preferences[key]) {
          preferences[key] = [];
        }
        preferences[key]!.push(channel as Channel);
      });

    return { preferences };
  };

  // Initialize preferences from candidateNotificationSettings
  useEffect(() => {
    const savePreferences = convertToPreferences(
      clientNotificationSettings || []
    );
    setPreferences(savePreferences.preferences);

    setCandidateAcceptedScheduledVacancyChecked(
      !!savePreferences?.preferences?.[
        "candidate_accepted_scheduled_vacancy"
      ] &&
        savePreferences.preferences["candidate_accepted_scheduled_vacancy"]!
          .length > 0
    );

    setCandidateAcceptedUrgentVacancyChecked(
      !!savePreferences?.preferences?.["candidate_accepted_urgent_vacancy"] &&
        savePreferences.preferences["candidate_accepted_urgent_vacancy"]!
          .length > 0
    );

    setCandidateAppliedScheduledVacancyChecked(
      !!savePreferences?.preferences?.["candidate_applied_scheduled_vacancy"] &&
        savePreferences.preferences["candidate_applied_scheduled_vacancy"]!
          .length > 0
    );

    setCandidateAppliedUrgentVacancyChecked(
      !!savePreferences?.preferences?.["candidate_applied_urgent_vacancy"] &&
        savePreferences.preferences["candidate_applied_urgent_vacancy"]!
          .length > 0
    );
  }, [clientNotificationSettings]);

  const handleLeave = () => {
    hanldeClose();
    if (!pendingAction) return;

    // Force the previously blocked action
    if (pendingAction === "reload") {
      window.location.reload();
    } else if (pendingAction === "back") {
      window.history.go(-1);
    } else if (pendingAction === "forward") {
      window.history.go(1);
    } else if (pendingAction.startsWith("/")) {
      window.location.href = pendingAction;
    }

    setPendingAction(null);
  };

  const handleSave = async () => {
    await handleSubmit();
    handleLeave();
  };

  const hanldeClose = () => {
    setShowConfirmUnSaved(false);
  };

  const getTimeZoneInfo = () => {
    // Define the current date
    const now: Date = new Date();

    // Define the options for the formatter
    const options: Intl.DateTimeFormatOptions = {
      timeZoneName: "long",
      timeZone: "Australia/Sydney", // AEDT timezone
    };

    // Create a formatter with the specified options
    const formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(
      "en-US",
      options
    );

    // Extract formatted timezone details
    const parts: Intl.DateTimeFormatPart[] = formatter.formatToParts(now);

    // Find the part that contains the time zone name
    const timeZoneName: string | undefined = parts.find(
      (part) => part.type === "timeZoneName"
    )?.value;

    // Calculate the offset in hours
    const offsetMinutes: number = -now.getTimezoneOffset();
    const offsetHours: number = offsetMinutes / 60;

    // const timeZone: string = `AEDT (${timeZoneName}) UTC/GMT ${
    //   offsetHours >= 0 ? "+" : ""
    // }${offsetHours} hours`;
    const timeZone: string = `AEDT (Australian Eastern Daylight Time) UTC/GMT ${
      offsetHours >= 0 ? "+" : ""
    }${offsetHours} hours`;
    // Log the timezone information
    return timeZone;
  };

  return (
    <>
      <div className="d-flex flex-column gap-15">
        <div className="h-1px"></div>
        <div className="fs-22 fw-400 text-black">Account Details</div>
        <div className="d-flex">
          <div className="border border-off-white radius-8px p-20px d-flex flex-column gap-10">
            <div className="d-flex gap-40 flex-wrap">
              <div className="d-flex flex-column gap-5">
                <div className="fs-13 fw-400 text-grey-medium">First Name</div>
                <div className="fs-16 fw-400 text-black">
                  {user?.first_name}
                </div>
              </div>
              <div className="d-flex flex-column gap-5">
                <div className="fs-13 fw-400 text-grey-medium">Last Name</div>
                <div className="fs-16 fw-400 text-black">{user?.last_name}</div>
              </div>
              <div className="d-flex flex-column gap-5">
                <div className="fs-13 fw-400 text-grey-medium">Email</div>
                <div className="fs-16 fw-400 text-black">
                  {user?.email || ""}
                </div>
              </div>
              <div className="d-flex flex-column gap-5">
                <div className="fs-13 fw-400 text-grey-medium">
                  Phone Number
                </div>
                <div className="fs-16 fw-400 text-black">
                  {user?.phone_number}
                </div>
              </div>
              <div className="w-1px h-1px"></div>
              {/* <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-off-white text-black max-h-30px align-self-center"
              >
                Edit Details
              </CustomButton> */}
            </div>
            <div className="d-flex gap-40 justify-content-between">
              <div className="d-flex flex-column gap-5">
                <div className="fs-13 fw-400 text-grey-medium">Password</div>
                <div className="fs-16 fw-400 text-black">************</div>
              </div>
              {/* <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-off-white text-black max-h-30px align-self-center"
              >
                Change Password
              </CustomButton> */}
            </div>
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 fw-400 text-grey-medium">
                Timezone (based on state location)
              </div>
              <div className="fs-16 fw-400 text-black">{getTimeZoneInfo()}</div>
            </div>
          </div>
        </div>
        <div className="h-1px"></div>
        <div className="bg-off-white h-1px w-100"></div>
        <div className="h-1px"></div>
        <div className="d-flex gap-20 justify-content-between">
          <div className="d-flex gap-10 align-items-center align-self-start">
            <div className="fs-22 fw-400 text-black">Notification Settings</div>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.16669 5.83268H10.8334V7.49935H9.16669V5.83268ZM9.16669 9.16602H10.8334V14.166H9.16669V9.16602ZM10 1.66602C5.40002 1.66602 1.66669 5.39935 1.66669 9.99935C1.66669 14.5993 5.40002 18.3327 10 18.3327C14.6 18.3327 18.3334 14.5993 18.3334 9.99935C18.3334 5.39935 14.6 1.66602 10 1.66602ZM10 16.666C6.32502 16.666 3.33335 13.6743 3.33335 9.99935C3.33335 6.32435 6.32502 3.33268 10 3.33268C13.675 3.33268 16.6667 6.32435 16.6667 9.99935C16.6667 13.6743 13.675 16.666 10 16.666Z"
                fill="#757575"
              />
            </svg>
            <div className="fs-13 text-grey-medium">
              Settings and summaries are for all your centres
            </div>
          </div>
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-teal text-black"
            onClick={handleSubmit}
          >
            <RoundedTick />
            {saveButtonText}
          </CustomButton>
        </div>
        <div className="d-flex flex-column gap-10">
          <div className="fs-16 fw-400 text-black">
            Select which events you want notifications for. Please note, some
            are mandatory. Daily summaries are sent 5pm your local timezone and
            weekly summaries are sent every Friday 5pm. If multiple daily and
            weekly summaries are selected, this will be delivered as one daily
            email or one weekly email.
          </div>
        </div>
        <div className="border border-off-white radius-8px p-20px d-flex gap-60">
          <div className="d-flex flex-column gap-8 align-self-end min-w-360px">
            <div className="fs-16 fw-400 text-black">
              Notify me when a candidate has{" "}
              <span className="fw-700">accepted</span>:
            </div>
            <div className="theme-check">
              <Checkbox
                themeCheck={true}
                className="theme-check-input"
                id="check_01"
                label="A scheduled vacancy"
                labelClass="theme-check-label fs-16 checked-teal ps-24px"
                labelLeft={false}
                labelFontSize="16"
                checked={candidateAcceptedScheduledVacancyChecked}
                onChange={(checked) =>
                  handleVacancyTypeChange(
                    "candidate_accepted_scheduled_vacancy",
                    setCandidateAcceptedScheduledVacancyChecked,
                    checked
                  )
                }
                // disabled={!scheduledVacancyChecked}
              />
            </div>
            <div className="theme-check">
              <Checkbox
                themeCheck={true}
                className="theme-check-input"
                id="check_02"
                label="An urgent vacancy (within the next 24 hours)"
                labelClass="theme-check-label fs-16 checked-teal ps-24px"
                labelLeft={false}
                labelFontSize="16"
                checked={candidateAcceptedUrgentVacancyChecked}
                onChange={(checked) =>
                  handleVacancyTypeChange(
                    "candidate_accepted_urgent_vacancy",
                    setCandidateAcceptedUrgentVacancyChecked,
                    checked
                  )
                }
                // disabled={!candidateAcceptedUrgentVacancyChecked}
              />
            </div>
          </div>
          <div className="w-1px bg-grey-light"></div>
          <div className="d-flex justify-content-between w-100 text-center">
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send SMS
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_03"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_accepted_scheduled_vacancy?.includes(
                      "sms"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_accepted_scheduled_vacancy",
                      "sms",
                      checked
                    )
                  }
                  disabled={!candidateAcceptedScheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_04"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_accepted_urgent_vacancy?.includes(
                      "sms"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_accepted_urgent_vacancy",
                      "sms",
                      checked
                    )
                  }
                  disabled={true}
                  // disabled={!candidateAcceptedUrgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send Email
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_05"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_accepted_scheduled_vacancy?.includes(
                      "email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_accepted_scheduled_vacancy",
                      "email",
                      checked
                    )
                  }
                  disabled={!candidateAcceptedScheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_06"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_accepted_urgent_vacancy?.includes(
                      "email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_accepted_urgent_vacancy",
                      "email",
                      checked
                    )
                  }
                  disabled={true}
                  // disabled={!candidateAcceptedUrgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Daily Email
                <br />
                Summary
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_07"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_accepted_scheduled_vacancy?.includes(
                      "daily_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_accepted_scheduled_vacancy",
                      "daily_summary_email",
                      checked
                    )
                  }
                  disabled={!candidateAcceptedScheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_08"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_accepted_urgent_vacancy?.includes(
                      "daily_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_accepted_urgent_vacancy",
                      "daily_summary_email",
                      checked
                    )
                  }
                  disabled={!candidateAcceptedUrgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Weekly Email
                <br />
                Summary
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_09"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_accepted_scheduled_vacancy?.includes(
                      "weekly_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_accepted_scheduled_vacancy",
                      "weekly_summary_email",
                      checked
                    )
                  }
                  disabled={!candidateAcceptedScheduledVacancyChecked}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="border border-off-white radius-8px p-20px d-flex gap-60">
          <div className="d-flex flex-column gap-8 align-self-end min-w-360px">
            <div className="fs-16 fw-400 text-black">
              Notify me when a candidate has{" "}
              <span className="fw-700">applied for</span>:
            </div>
            <div className="theme-check">
              <Checkbox
                themeCheck={true}
                className="theme-check-input"
                id="check_11"
                label="A scheduled vacancy"
                labelClass="theme-check-label fs-16 checked-teal ps-24px"
                labelLeft={false}
                labelFontSize="16"
                checked={candidateAppliedScheduledVacancyChecked}
                onChange={(checked) =>
                  handleVacancyTypeChange(
                    "candidate_applied_scheduled_vacancy",
                    setCandidateAppliedScheduledVacancyChecked,
                    checked
                  )
                }
              />
            </div>
            <div className="theme-check">
              <Checkbox
                themeCheck={true}
                className="theme-check-input"
                id="check_12"
                label="An urgent vacancy (within the next 24 hours)"
                labelClass="theme-check-label fs-16 checked-teal ps-24px"
                labelLeft={false}
                labelFontSize="16"
                checked={candidateAppliedUrgentVacancyChecked}
                onChange={(checked) =>
                  handleVacancyTypeChange(
                    "candidate_applied_urgent_vacancy",
                    setCandidateAppliedUrgentVacancyChecked,
                    checked
                  )
                }
              />
            </div>
          </div>
          <div className="w-1px bg-grey-light"></div>
          <div className="d-flex justify-content-between w-100 text-center">
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send SMS
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_13"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_applied_scheduled_vacancy?.includes(
                      "sms"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_applied_scheduled_vacancy",
                      "sms",
                      checked
                    )
                  }
                  disabled={!candidateAppliedScheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_14"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_applied_urgent_vacancy?.includes(
                      "sms"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_applied_urgent_vacancy",
                      "sms",
                      checked
                    )
                  }
                  disabled={true}
                  // disabled={!candidateAppliedUrgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send Email
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_15"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_applied_scheduled_vacancy?.includes(
                      "email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_applied_scheduled_vacancy",
                      "email",
                      checked
                    )
                  }
                  disabled={!candidateAppliedScheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_16"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_applied_urgent_vacancy?.includes(
                      "email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_applied_urgent_vacancy",
                      "email",
                      checked
                    )
                  }
                  disabled={true}
                  // disabled={!candidateAppliedUrgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Daily Email
                <br />
                Summary
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_17"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_applied_scheduled_vacancy?.includes(
                      "daily_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_applied_scheduled_vacancy",
                      "daily_summary_email",
                      checked
                    )
                  }
                  disabled={!candidateAppliedScheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_18"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_applied_urgent_vacancy?.includes(
                      "daily_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_applied_urgent_vacancy",
                      "daily_summary_email",
                      checked
                    )
                  }
                  disabled={!candidateAppliedUrgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Weekly Email
                <br />
                Summary
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_19"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_applied_scheduled_vacancy?.includes(
                      "weekly_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_applied_scheduled_vacancy",
                      "weekly_summary_email",
                      checked
                    )
                  }
                  disabled={!candidateAppliedScheduledVacancyChecked}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="border border-off-white radius-8px p-20px d-flex gap-60">
          <div className="d-flex flex-column gap-8 align-self-end min-w-360px max-w-360px">
            <div className="fs-16 fw-400 text-black">
              If a candidate declines a vacancy after accepting or after I have
              chosen them:
            </div>
          </div>
          <div className="w-1px bg-grey-light"></div>
          <div className="d-flex justify-content-between w-100 text-center">
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send SMS
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_31"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_declined_job?.includes("sms") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_declined_job",
                      "sms",
                      checked
                    )
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send Email
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_32"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.candidate_declined_job?.includes("email") ||
                    false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "candidate_declined_job",
                      "email",
                      checked
                    )
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Daily Email
                <br />
                Summary
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Weekly Email
                <br />
                Summary
              </div>
            </div>
          </div>
        </div>
        <div className="border border-off-white radius-8px p-20px d-flex gap-60">
          <div className="d-flex flex-column gap-8 align-self-end min-w-360px">
            <div className="fs-16 fw-400 text-black">
              If I have timesheets pending my approval:
            </div>
          </div>
          <div className="w-1px bg-grey-light"></div>
          <div className="d-flex justify-content-between w-100 text-center">
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send SMS
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_41"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.pending_timesheet_approval?.includes("sms") ||
                    false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "pending_timesheet_approval",
                      "sms",
                      checked
                    )
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send Email
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_42"
                  label=""
                  labelClass={`fs-16 checked-grey-light-bg-grey ps-24px`}
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.pending_timesheet_approval?.includes("email") ||
                    false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "pending_timesheet_approval",
                      "email",
                      checked
                    )
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Daily Email
                <br />
                Summary
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Weekly Email
                <br />
                Summary
              </div>
            </div>
          </div>
        </div>
      </div>
      <UnsavedChangesModal
        visible={showConfirmUnSaved}
        onLeave={handleLeave}
        onSave={handleSave}
        onClose={hanldeClose}
      />
    </>
  );
};

export default Settings;
